/* eslint-disable */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { usePortalCounts } from './usePortalCounts';

// Brief F — per-portal subsection counts for the hamburger drawer.
//
// Phase 1 strategy: reuse the existing `usePortalCounts` (itemsTotal,
// devsTotal, qcReports, salesItemsWithSales/LatestBatch) and layer on a
// few targeted COUNT queries for sub-views that are not yet covered:
//
//   - dev_requests open    (Originals → Dev Requests trail)
//   - qc_reports pending   (QC → Manager Review trail)
//   - sales_batches total  (Sales → Batches trail)
//
// Per-user counts (Favorites) intentionally return `null` in Phase 1 —
// the prototype renders `null` counts as just the label with no trail
// pill (see CatalogDrawer rendering). The Phase 1 contract is: the
// hook never throws, missing counts are `null`, and the drawer renders
// gracefully against partial data while we expand coverage later.
//
// Refetch budget: shares `usePortalCounts` 60s staleTime / 5min gc;
// the extra 3 COUNT queries also use 60s staleTime. All counts use
// `count: 'exact', head: true` so only metadata returns (no row blobs).

export function useSubsectionCounts(enabled = true) {
  const portalCounts = usePortalCounts(enabled);

  const extras = useQuery({
    queryKey: ['subsection-counts-extras'],
    queryFn: async () => {
      const [devRequestsOpen, qcManagerReview, salesBatches] = await Promise.all([
        // Dev Requests open queue — count rows that are not yet linked
        // to a development. Status names are not enforced uniformly; the
        // `development_id IS NULL` predicate is the canonical "open" gate.
        // development_id is the FK to developments.id (constraint
        // dev_requests_development_id_fkey); it is set only when a request
        // is accepted and a development is created from it
        // (DevelopmentsTab.js), so NULL means "not yet linked".
        supabase
          .from('dev_requests')
          .select('*', { count: 'exact', head: true })
          .is('development_id', null),

        // QC Manager Review queue — reports awaiting manager action.
        // Production status enum includes 'pending' for awaiting review.
        supabase
          .from('qc_reports')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending'),

        // Sales days — total uploaded days (renamed from sales_batches in WS-1).
        supabase
          .from('sales_days')
          .select('*', { count: 'exact', head: true }),
      ]);

      // Per Phase 1 contract: never throw; on any error, return null
      // for that count and let the drawer hide the trail pill.
      return {
        devRequestsOpen: devRequestsOpen.error ? null : (devRequestsOpen.count ?? null),
        qcManagerReview: qcManagerReview.error ? null : (qcManagerReview.count ?? null),
        salesBatches:    salesBatches.error    ? null : (salesBatches.count ?? null),
      };
    },
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Compose the per-portal shape the drawer renders against. Keys mirror
  // the prototype subsection list order — see docs/prototypes/
  // originals-chrome-redesign-v11.html lines 2406–2457.
  const pc = portalCounts.data || {};
  const ex = extras.data || {};

  const data = {
    catalog: {
      items:        pc.itemsTotal ?? null,
      dashboard:    null,
      favorites:    null, // Phase 1: per-user counts deferred
      analytics:    null,
      dev_requests: ex.devRequestsOpen ?? null,
    },
    developments: {
      board:       pc.devsTotal ?? null,
      list:        null,
      byDesigner:  null,
      analytics:   null,
      archive:     null,
      settings:    null,
    },
    qc: {
      dashboard:      null,
      reports:        pc.qcReports ?? null,
      managerReview:  ex.qcManagerReview ?? null,
      analytics:      null,
      settings:       null,
    },
    sales: {
      overview:    null,
      batches:     ex.salesBatches ?? null,
      modelFamily: null,
      analytics:   null,
    },
  };

  return {
    data,
    isLoading: portalCounts.isLoading || extras.isLoading,
    error:     portalCounts.error || extras.error || null,
  };
}
